import { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import {
	verifyEmail as verifyEmailApi,
	forgotPassword as forgotPasswordApi,
	resetPassword as resetPasswordApi,
	updateProfile,
} from "../../services/api";
import toast from "react-hot-toast";
import { Eye, EyeOff } from "lucide-react";
import { signInWithPopup, GoogleAuthProvider, OAuthProvider } from "firebase/auth";
import { auth, googleProvider, appleProvider } from "../../config/firebase";
import googleLogo from "../../assets/google-icon-logo-svgrepo-com.svg";
import appleLogo from "../../assets/apple-black-logo-svgrepo-com.svg";
import "./Auth.css";

const Login = () => {
	const navigate = useNavigate();
	const { login, verifyEmail, resendOtp, authLoading, user, loginWithGoogle, loginWithApple } = useAuth();
	const [formValues, setFormValues] = useState({ email: "", password: "" });
	const [formError, setFormError] = useState(null);

	const [otp, setOtp] = useState("");
	const [steps, setSteps] = useState("login"); // login, enter_email_verify, enter_otp, forgot_password_request, forgot_password_verify
	const [localLoading, setLocalLoading] = useState(false);
	const [newPassword, setNewPassword] = useState("");
	const [confirmPassword, setConfirmPassword] = useState("");
	const [showNewPassword, setShowNewPassword] = useState(false);
	const [showConfirmPassword, setShowConfirmPassword] = useState(false);
	const [showLoginPassword, setShowLoginPassword] = useState(false);
	const [googleLoading, setGoogleLoading] = useState(false);
	const [appleLoading, setAppleLoading] = useState(false);

	useEffect(() => {
		if (user) {
			navigate(user?.isProfileComplete ? "/app" : "/profile", { replace: true });
		}
	}, [user, navigate]);

	const handleChange = (event) => {
		const { name, value } = event.target;
		setFormValues((prev) => ({ ...prev, [name]: value }));
	};

	const handleSubmit = async (event) => {
		event.preventDefault();
		setFormError(null);

		try {
			await login(formValues);
		} catch (error) {
			if (error?.message?.includes("verify your email")) {
				setFormError(
					<>
						{error.message}{" "}
						<button
							onClick={() => {
								setFormError(null);
								setSteps("enter_email_verify");
							}}
							className='underline font-bold hover:text-red-800'
						>
							Verify now
						</button>
					</>,
				);
			} else {
				setFormError(error?.message || "Unable to log in. Please try again.");
			}
		}
	};

	const handleGoogleLogin = useCallback(() => {
		setGoogleLoading(true);
		signInWithPopup(auth, googleProvider)
			.then(async (result) => {
				const credential = GoogleAuthProvider.credentialFromResult(result);
				const tokenId = credential?.idToken;
				const fullName = result?.user?.displayName || undefined;
				const email = result?.user?.email || undefined;
				if (!tokenId) {
					throw new Error("Google token not found");
				}
				const loggedInUser = await loginWithGoogle({
					tokenId,
					name: fullName,
					email,
					provider: "google",
				});

				if (!loggedInUser?.name && fullName) {
					try {
						await updateProfile({ name: fullName });
					} catch (_error) {
						// Ignore non-blocking name sync errors.
					}
				}
			})
			.catch((error) => {
				const message = error?.message || "";
				if (!/cancel/i.test(message) && !/popup/i.test(message)) {
					toast.error("Google sign-in failed. Please try again.");
				}
				setGoogleLoading(false);
			})
			.finally(() => {
				setGoogleLoading(false);
			});
	}, [loginWithGoogle]);

	const handleAppleLogin = useCallback(async () => {
		setAppleLoading(true);
		try {
			const result = await signInWithPopup(auth, appleProvider);
			const credential = OAuthProvider.credentialFromResult(result);
			const appleId = credential?.idToken || result?.user?.uid;
			const email = result?.user?.email || undefined;
			const name = result?.user?.displayName || undefined;

			if (!appleId) {
				throw new Error("Apple token not found");
			}

			await loginWithApple({ appleId, email, name });
		} catch (error) {
			const message = error?.message || "";
			if (!/cancel/i.test(message) && !/popup/i.test(message)) {
				if (error?.code === "auth/operation-not-allowed") {
					toast.error("Apple Sign-In is not enabled in Firebase Auth for this project.");
				} else {
					toast.error(error?.message || "Apple sign-in failed. Please try again.");
				}
			}
		} finally {
			setAppleLoading(false);
		}
	}, [loginWithApple]);

	const handleSendVerification = async (e) => {
		e.preventDefault();
		setFormError(null);
		try {
			await resendOtp({ email: formValues.email });
			setSteps("enter_otp");
		} catch (error) {
			setFormError(error?.message || "Failed to send OTP.");
		}
	};

	const handleVerifyOtp = async (e) => {
		e.preventDefault();
		setFormError(null);
		setLocalLoading(true);
		try {
			// 1. Verify OTP via direct API (check validity)
			await verifyEmailApi({ email: formValues.email, otp });

			// 2. Show Success Toast
			toast.success("Email Verified! Logging you in...");

			// 3. Immediately hydrate auth context and redirect
			// Delay slightly for UX? Or just go.
			// User says: "show a toast message... good UX".
			// If we redirect too fast, they won't see it.
			// Let's hydrate immediately, but maybe wait 1.5s to nav, or just nav.
			// Actually, if we hydrate, the 'user' effect might trigger nav.
			// Let's call context verify which updates 'user'.

			await verifyEmail({ email: formValues.email, otp });
			// The useEffect([user]) will handle redirect to /app
		} catch (error) {
			setFormError(error?.message || "Invalid OTP");
			setLocalLoading(false);
		}
		// We don't setLocalLoading(false) on success to keep button disabled while redirecting
	};

	const handleForgotPasswordRequest = async (e) => {
		e.preventDefault();
		setFormError(null);
		setLocalLoading(true);
		try {
			await forgotPasswordApi({ email: formValues.email });
			setSteps("forgot_password_verify");
			toast.success("Reset code sent to your email");
		} catch (error) {
			setFormError(error?.message || "Failed to send reset code.");
		} finally {
			setLocalLoading(false);
		}
	};

	const handleResetPassword = async (e) => {
		e.preventDefault();
		setFormError(null);
		setLocalLoading(true);

		if (newPassword.length < 6) {
			setFormError("Password must be at least 6 characters.");
			setLocalLoading(false);
			return;
		}

		if (newPassword !== confirmPassword) {
			setFormError("Passwords do not match.");
			setLocalLoading(false);
			return;
		}

		try {
			await resetPasswordApi({ email: formValues.email, otp, newPassword });
			toast.success("Password reset successfully! Please login.");
			setSteps("login");
			setNewPassword("");
			setConfirmPassword("");
			setOtp("");
			// Retain email for easier login
		} catch (error) {
			setFormError(error?.message || "Failed to reset password.");
		} finally {
			setLocalLoading(false);
		}
	};

	// Render main content based on step
	let content = null;

	if (steps === "enter_email_verify") {
		content = (
			<>
				<h1>Verify Email</h1>
				<p>Enter your email to receive a verification code.</p>
				<form className='auth-form' onSubmit={handleSendVerification}>
					<label>
						Email
						<input
							type='email'
							name='email'
							placeholder='you@example.com'
							required
							value={formValues.email}
							onChange={handleChange}
						/>
					</label>
					{formError && <p className='auth-error'>{formError}</p>}
					<button
						type='submit'
						className={`auth-submit ${authLoading ? "loading" : ""}`}
						disabled={authLoading}
					>
						{authLoading ? "Sending..." : "Send OTP"}
					</button>
				</form>
				<button className='auth-link mt-4' onClick={() => setSteps("login")}>
					Back to Login
				</button>
			</>
		);
	} else if (steps === "enter_otp") {
		content = (
			<>
				<h1>Enter Verification Code</h1>
				<p>Code sent to {formValues.email}</p>
				<form className='auth-form' onSubmit={handleVerifyOtp}>
					<input
						type='text'
						value={otp}
						onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
						placeholder='000000'
						className='text-center text-2xl tracking-widest p-3 rounded-lg border border-slate-300 focus:border-indigo-500 outline-none w-full'
						required
					/>
					{formError && <p className='auth-error'>{formError}</p>}
					<button
						type='submit'
						className={`auth-submit ${localLoading ? "loading" : ""}`}
						disabled={localLoading}
					>
						{localLoading ? "Verifying..." : "Verify"}
					</button>
				</form>
				<button
					className='auth-link mt-4'
					onClick={() => setSteps("enter_email_verify")}
				>
					Change Email
				</button>
			</>
		);
	} else if (steps === "forgot_password_request") {
		content = (
			<>
				<h1>Reset Password</h1>
				<p>Enter your email to receive a reset code.</p>
				<form className='auth-form' onSubmit={handleForgotPasswordRequest}>
					<label>
						Email
						<input
							type='email'
							name='email'
							placeholder='you@example.com'
							required
							value={formValues.email}
							onChange={handleChange}
						/>
					</label>
					{formError && <p className='auth-error'>{formError}</p>}
					<button
						type='submit'
						className={`auth-submit ${localLoading ? "loading" : ""}`}
						disabled={localLoading}
					>
						{localLoading ? "Sending..." : "Send Reset Code"}
					</button>
				</form>
				<button className='auth-link mt-4' onClick={() => setSteps("login")}>
					Back to Login
				</button>
			</>
		);
	} else if (steps === "forgot_password_verify") {
		content = (
			<>
				<h1>Set New Password</h1>
				<p>Enter the code sent to {formValues.email} and your new password.</p>
				<form className='auth-form' onSubmit={handleResetPassword}>
					<label>
						Verification Code
						<input
							type='text'
							value={otp}
							onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
							placeholder='000000'
							className='text-center tracking-widest p-3 rounded-lg border border-slate-300 focus:border-indigo-500 outline-none w-full'
							required
						/>
					</label>
					<label>
						New Password
						<div className='relative'>
							<input
								type={showNewPassword ? "text" : "password"}
								value={newPassword}
								onChange={(e) => setNewPassword(e.target.value)}
								placeholder='New Password'
								className='w-full pr-10'
								required
								minLength={6}
							/>
							<button
								type='button'
								onClick={() => setShowNewPassword(!showNewPassword)}
								className='absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600'
							>
								{showNewPassword ? <EyeOff size={20} /> : <Eye size={20} />}
							</button>
						</div>
					</label>
					<label>
						Confirm New Password
						<div className='relative'>
							<input
								type={showConfirmPassword ? "text" : "password"}
								value={confirmPassword}
								onChange={(e) => setConfirmPassword(e.target.value)}
								placeholder='Confirm New Password'
								className='w-full pr-10'
								required
								minLength={6}
							/>
							<button
								type='button'
								onClick={() => setShowConfirmPassword(!showConfirmPassword)}
								className='absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600'
							>
								{showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
							</button>
						</div>
					</label>
					{formError && <p className='auth-error'>{formError}</p>}
					<button
						type='submit'
						className={`auth-submit ${localLoading ? "loading" : ""}`}
						disabled={localLoading}
					>
						{localLoading ? "Resetting..." : "Reset Password"}
					</button>
				</form>
				<div className='flex flex-col mt-4 gap-2'>
					<button
						className='auth-link'
						onClick={() => setSteps("forgot_password_request")}
					>
						Change Email
					</button>
					<button className='auth-link' onClick={() => setSteps("login")}>
						Back to Login
					</button>
				</div>
			</>
		);
	} else {
		// Default: Login step
		content = (
			<>
				<h1>Welcome back</h1>
				<p>Sign in to access your saved preferences and settings.</p>
				<form className='auth-form' onSubmit={handleSubmit}>
					<label>
						Email
						<input
							type='email'
							name='email'
							placeholder='you@example.com'
							required
							value={formValues.email}
							onChange={handleChange}
						/>
					</label>
					<label>
						Password
						<div className='relative'>
							<input
								type={showLoginPassword ? "text" : "password"}
								name='password'
								placeholder='********'
								required
								value={formValues.password}
								onChange={handleChange}
								className='w-full pr-10'
							/>
							<button
								type='button'
								onClick={() => setShowLoginPassword(!showLoginPassword)}
								className='absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600'
							>
								{showLoginPassword ? <EyeOff size={20} /> : <Eye size={20} />}
							</button>
						</div>
					</label>
					{formError && <p className='auth-error'>{formError}</p>}
					<button
						type='submit'
						className={`auth-submit ${authLoading ? "loading" : ""}`}
						disabled={authLoading || googleLoading || appleLoading}
					>
						{authLoading ? "Signing in..." : "Log in"}
					</button>
				</form>
				<div className='auth-social-separator'>Or continue with</div>
				<div className='auth-social-buttons'>
					<button
						type='button'
						className='auth-social-button google'
						onClick={handleGoogleLogin}
						disabled={authLoading || googleLoading || appleLoading}
					>
						<span className='auth-social-icon google' aria-hidden='true'>
							<img src={googleLogo} alt='' width={20} height={20} />
						</span>
						<span>{googleLoading ? "Connecting to Google..." : "Continue with Google"}</span>
					</button>
					<button
						type='button'
						className='auth-social-button apple'
						onClick={handleAppleLogin}
						disabled={authLoading || googleLoading || appleLoading}
					>
						<span className='auth-social-icon apple' aria-hidden='true'>
							<img src={appleLogo} alt='' width={20} height={20} />
						</span>
						<span>{appleLoading ? "Connecting to Apple..." : "Continue with Apple"}</span>
					</button>
				</div>
				<div className='flex flex-col gap-2 mt-4 text-center'>
					<button
						onClick={() => {
							setFormError(null);
							setSteps("forgot_password_request");
						}}
						className='text-sm text-indigo-600 hover:text-indigo-800 font-medium'
					>
						Forgot Password?
					</button>

					<p className='auth-note'>
						Need an account?{" "}
						<Link to='/signup' className='auth-link'>
							Create one here.
						</Link>
					</p>

					<p className='text-xs text-slate-400 mt-1 leading-relaxed'>
						By logging in, you agree to our{" "}
						<Link
							to='/terms-conditions'
							className='text-indigo-500 hover:text-indigo-700 underline underline-offset-2'
						>
							Terms &amp; Conditions
						</Link>{" "}
						and{" "}
						<Link
							to='/privacy-policy'
							className='text-indigo-500 hover:text-indigo-700 underline underline-offset-2'
						>
							Privacy Policy
						</Link>
						.
					</p>
				</div>
			</>
		);
	}

	return <div className='auth-card'>{content}</div>;
};

export default Login;
